/**
 * Protected Route Component
 * Redirects to login if not authenticated
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireEmailVerification?: boolean;
}

// Debug logging helper
function debugLog(step: string, data?: unknown) {
  const logs = JSON.parse(localStorage.getItem('vizzex_debug_log') || '[]');
  logs.push({
    time: new Date().toISOString(),
    step: `[ProtectedRoute] ${step}`,
    data: data ? JSON.stringify(data) : undefined
  });
  if (logs.length > 50) logs.shift();
  localStorage.setItem('vizzex_debug_log', JSON.stringify(logs));
}

export function ProtectedRoute({ children, requireEmailVerification = false }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  debugLog('render', { isAuthenticated, isLoading, path: location.pathname });

  // Show loading spinner while checking auth
  if (isLoading) {
    debugLog('showing loading spinner');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-[var(--color-primary-400)] animate-spin" />
          <p className="text-[var(--text-secondary)]">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    debugLog('NOT AUTHENTICATED - redirecting to /login', { from: location.pathname });
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check email verification if required
  if (requireEmailVerification && user && !user.emailVerified) {
    debugLog('Email not verified - redirecting to /verify-email');
    return <Navigate to="/verify-email" state={{ from: location }} replace />;
  }

  debugLog('Authenticated, rendering children');
  return <>{children}</>;
}

export default ProtectedRoute;


